import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { MilestoneTrophy } from '../assets';
import { RATE_IT_MILESTONE_COLORS, RATE_IT_MILESTONE_LAYOUT } from '../constants';

const rise = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  /* Above the card's own header and bottom stack, so nothing on the moment sits over this. */
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  text-align: center;
  animation: ${fade} 200ms ease-out both;

  /* The blur and its tint are one unit. backdrop-filter is the web's BlurView — already used
     elsewhere in this app — and the flat tint is what a browser without it falls back to.
     (No backticks in here: this is a styled-components template literal.) */
  background: ${RATE_IT_MILESTONE_LAYOUT.SCRIM};
  backdrop-filter: blur(${RATE_IT_MILESTONE_LAYOUT.BLUR_PX}px);
  -webkit-backdrop-filter: blur(${RATE_IT_MILESTONE_LAYOUT.BLUR_PX}px);
`;

const Celebration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${rise} 260ms cubic-bezier(0.22, 1, 0.36, 1) both;
  line-height: 0;
`;

const Title = styled.h2`
  margin: ${RATE_IT_MILESTONE_LAYOUT.TITLE_GAP}px 0 0;
  font-family: CeraPro, sans-serif;
  font-weight: 700;
  font-size: ${RATE_IT_MILESTONE_LAYOUT.TITLE_SIZE}px;
  line-height: ${RATE_IT_MILESTONE_LAYOUT.TITLE_LINE_HEIGHT}px;
  color: ${RATE_IT_MILESTONE_COLORS.WHITE};
`;

const Continue = styled.button`
  margin-top: 28px;
  padding: 12px 28px;
  min-height: 44px;
  border: 0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.12);
  color: ${RATE_IT_MILESTONE_COLORS.WHITE};
  font-family: CeraPro, sans-serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 1px;
  cursor: pointer;
  animation: ${rise} 320ms cubic-bezier(0.22, 1, 0.36, 1) both;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

/**
 * The every-5-completions celebration: the trophy and "You rated N moments!" over the blurred
 * moment. The cycle is shared with Tag It in the app, so "rated" is only true because
 * `RateItFeed` raises this for an all-ratings cycle and skips it otherwise.
 *
 * MOBILE'S MILESTONE WITHOUT ITS INVITE. The badge, the headline, the blur and the copy are
 * mobile's `RateItMilestoneModal` verbatim; what is deliberately absent is everything below the
 * headline — the bottom sheet, the "both earn N points" line, the Invite Friends CTA and the
 * NOT NOW that existed to decline it. This surface is the far END of that invite: the fan
 * looking at it arrived on someone's referral link, so asking them to mint another one here
 * inverts the flow. Keeping the mobile filename is what keeps the two checkable side by side.
 *
 * Says nothing about points, and takes no amount. The every-5 participation cycle pays silently,
 * and a fifth rating that completes a referral pays BOTH fans server-side — neither figure is on
 * the wire (`POST /tiri/rateit/submit` carries no reward fields at all, by design), so there is
 * nothing here to quote even if the design wanted it. This is a confirmation, not a receipt.
 *
 * ONE WAY OUT, and the scrim is not it: the overlay covers the pager and swallows the wheel and
 * touch events that would otherwise scroll the feed underneath, so the fan leaves through
 * CONTINUE. That matches the feed's own behaviour on a milestone — the advance is held, so
 * dismissing returns them to the very moment that earned it, with nothing to unwind.
 *
 * CONTINUE CARRIES NO `autoFocus`, and that is load-bearing rather than an omission. React
 * applies `autoFocus` by calling `node.focus()` from `commitMount` during the commit phase, which
 * synchronously dispatches focus events — and on a real device some listener outside this bundle
 * (an extension, a password manager) threw inside one. The DOM reports a listener's exception to
 * `window.onerror` rather than rethrowing it to the dispatcher, so nothing actually broke: React
 * committed, the modal rendered, the feed kept working. What the fan got was the dev server's
 * full-screen "Uncaught runtime errors" overlay on top of their celebration, with the message
 * masked to "Script error." because the throwing script was cross-origin.
 *
 * Nothing is lost by dropping it. There is one control in this dialog, so Tab reaches CONTINUE
 * immediately, and `autoFocus` without a focus trap was never real focus management anyway — on
 * iOS it mostly served to make the overlay scroll. Do not add it back to be helpful.
 */
const RateItMilestoneModal = ({ count, onClose }) => {
  const { t } = useTranslation();
  // `count` drives i18next's plural selection as well as the number in the sentence, so a cycle
  // length of 1 reads "1 moment!" — see `tiri.rateIt.milestone` in `en-app.js`.
  const title = t('tiri.rateIt.milestone.title', 'You rated {{count}} moments!', { count });

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // The celebration absorbs scroll and swipe so the pager cannot move behind it.
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <Celebration>
        {/* ONE asset — disc, ring and trophy together, straight from Figma — so there is no
            wrapper here giving it a background or a border. See `../assets/MilestoneTrophy`. */}
        {parse(MilestoneTrophy({ dimensions: RATE_IT_MILESTONE_LAYOUT.BADGE_SIZE }))}
        <Title>{title}</Title>
      </Celebration>

      {/* Deliberately NOT `autoFocus` — see the note on the overlay above. */}
      <Continue type="button" onClick={onClose}>
        {t('tiri.rateIt.milestone.continue', 'CONTINUE')}
      </Continue>
    </Overlay>
  );
};

export default RateItMilestoneModal;
